'use client';

import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Check, Loader2, Clock, ChevronRight, ShieldCheck, Globe2, KeyRound } from 'lucide-react';
import { Z } from '@/lib/z-index';
import { useAnimatedClose } from '@/lib/use-animated-close';
import type { CommunityPost } from '@/lib/community/types';
import { ABILITY_TYPE_CONFIG } from '@/lib/community/types';
import { DEMO_CONVERSATIONS, type DemoMessage } from '@/lib/community/demo-conversations';
import { AbilityTypeBadge } from './ability-type-badge';
import { RichCardRenderer } from '@/src/components/demo/rich-card-renderer';
import { ChatMarkdown } from '@/src/workplace/chat/components/chat-markdown';
import { AgentAvatar } from '@/src/components/agent/agent-avatar';
import { ToolCallItem } from '@/src/workplace/chat/components/blocks/tool-call-item';
import { matchToolCapsule } from '@/src/workplace/chat/components/blocks/tool-capsule-match';
import type { CapsuleMatchType } from '@/src/workplace/chat/components/blocks/tool-capsule-match';
import '@/src/workplace/chat/components/blocks/tool-call-item.less';
import { cn } from '@/lib/utils';

/* ── SSE 解析：将 aiMessage 文本解析为 ClaudeCodeMessage ── */
/* 与 agent-sse-service.ts 的 ClaudeCodeMessage 格式一致 */

interface SseContentItem {
  type: string;
  text?: string;
  thinking?: string;
  name?: string;
  id?: string;
  input?: Record<string, unknown>;
  description?: string;
  tool_use_id?: string;
  content?: string | unknown;
  safeclaw?: {
    status?: string;
    matchType?: string;
  };
}

interface ClaudeCodeMessage {
  type: string;
  message?: {
    content?: SseContentItem[];
  };
  result?: string;
  subtype?: string;
  safeclaw?: {
    messageId?: string;
    role?: string;
    timestamp?: number;
  };
}

/** 单条解析后的对话消息，用于渲染 */
interface ParsedChatMessage {
  role: 'user' | 'assistant';
  textParts: string[];
  toolUses: { id: string; name: string; description?: string; input?: Record<string, unknown>; matchType?: CapsuleMatchType }[];
  toolResults: { toolUseId: string; content: string }[];
  thinkingParts: string[];
  messageId: string;
}

/**
 * 解析 aiMessage 中的 SSE 帧，返回按消息边界聚合的对话列表。
 * SSE 帧格式：每行 `data: {...}`，帧之间空行分隔。
 * 遵循 agent-sse-service.ts 的解析逻辑。
 */
function parseAiMessage(sseText: string): ParsedChatMessage[] {
  const frames: ClaudeCodeMessage[] = [];

  // 逐行解析 SSE data 帧
  for (const line of sseText.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed.startsWith('data:')) continue;
    const jsonStr = trimmed.slice(5).trim();
    if (!jsonStr) continue;
    try {
      frames.push(JSON.parse(jsonStr));
    } catch {
      // skip malformed
    }
  }

  // 将帧聚合为消息 — 每个 SSE 帧都有独立的 messageId，应作为独立消息
  const messages: ParsedChatMessage[] = [];

  for (const frame of frames) {
    const role = frame.type === 'user' ? 'user' : frame.type === 'assistant' ? 'assistant' : null;
    if (!role) {
      // result / system / query_stop 等非对话帧，跳过
      continue;
    }

    const msgId = frame.safeclaw?.messageId;

    // 每个 frame 都创建独立消息（不同 messageId 或角色不同）
    const current: ParsedChatMessage = {
      role,
      textParts: [],
      toolUses: [],
      toolResults: [],
      thinkingParts: [],
      messageId: msgId ?? `msg-${messages.length}`,
    };
    messages.push(current);

    const contentItems = frame.message?.content;
    if (!Array.isArray(contentItems)) continue;

    const hasToolResult = contentItems.some((it) => it.type === 'tool_result');

    for (const item of contentItems) {
      if (item.type === 'text' && item.text) {
        // 含 tool_result 的 user 消息中的 text 是系统注入，跳过
        if (hasToolResult && role === 'user') continue;
        // 过滤系统注入长文本
        if (role === 'user' && item.text.length > 500) continue;
        current.textParts.push(item.text);
      } else if (item.type === 'thinking' && item.thinking) {
        current.thinkingParts.push(item.thinking);
      } else if (item.type === 'tool_use' && item.id && item.name) {
        current.toolUses.push({
          id: item.id,
          name: item.name,
          description: item.description,
          input: item.input as Record<string, unknown> | undefined,
          matchType: (item.safeclaw as Record<string, unknown> | undefined)?.matchType as CapsuleMatchType | undefined,
        });
      } else if (item.type === 'tool_result' && item.tool_use_id) {
        const text = typeof item.content === 'string' ? item.content : JSON.stringify(item.content);
        current.toolResults.push({ toolUseId: item.tool_use_id, content: text });
      }
    }
  }

  // 过滤空消息
  return messages.filter((m) => m.textParts.length > 0 || m.toolUses.length > 0 || m.toolResults.length > 0);
}

interface CasePreviewModalProps {
  post: CommunityPost;
  onClose: () => void;
}

/* ── SSE 消息气泡：完全复用 workplace ChatMessageView 的样式 ── */

function SseChatBubble({ msg }: { msg: ParsedChatMessage }) {
  // User 消息
  if (msg.role === 'user') {
    const text = msg.textParts.join('\n');
    if (!text) return null;
    return (
      <div className="group px-1 mb-3">
        <div className="flex justify-end">
          <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-[oklch(0.52_0.17_160)] px-4 py-2.5 shadow-lg">
            <div className="text-sm text-primary-foreground leading-relaxed">
              <ChatMarkdown content={text} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Assistant 消息 — 复用 workplace AiMessageBubble 的历史消息样式
  const hasToolUses = msg.toolUses.length > 0;
  const hasText = msg.textParts.length > 0;

  return (
    <div className="group px-1 mb-3">
      {/* Tool calls — 复用 workplace ToolCallItem 胶囊样式 + matchToolCapsule */}
      {hasToolUses && (
        <div className="flex gap-3 items-start mb-1.5">
          <div className="shrink-0 w-7" />
          <div className="flex-1 min-w-0 flex flex-col gap-0.5">
            {msg.toolUses.map((tool, i) => {
              // 优先使用 SSE 数据中的 safeclaw.matchType，fallback 到 matchToolCapsule 推断
              const match = tool.matchType
                ? { type: tool.matchType, label: tool.name, paramLabel: tool.description }
                : matchToolCapsule(tool.name, tool.input ?? {});
              return (
                <ToolCallItem
                  key={tool.id ?? i}
                  tool={{
                    id: tool.id,
                    label: match?.label ?? tool.name,
                    paramLabel: match?.paramLabel ?? tool.description,
                    status: 'done',
                    matchType: match?.type,
                  }}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Text content */}
      {hasText && (
        <div className="flex gap-3 items-start">
          <div className="shrink-0 mt-0.5 w-7">
            <AgentAvatar />
          </div>
          <div className="rounded-2xl rounded-tl-sm bg-secondary/50 backdrop-blur-sm border border-border/40 px-4 py-2.5 shadow-lg w-fit max-w-[calc(100%-3rem)]">
            {msg.textParts.map((text, i) => (
              <div className={cn('text-foreground', i > 0 && 'mt-3')} key={i} style={{ wordBreak: 'break-word' }}>
                <ChatMarkdown content={text} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Demo 消息气泡：保留原有渲染逻辑 ── */

const PRIVACY_ICON: Record<string, typeof ShieldCheck> = {
  local: ShieldCheck,
  'local-optional': ShieldCheck,
  external: Globe2,
  'auth-required': KeyRound,
  'security-tool': ShieldCheck,
};

const PRIVACY_ORB_STYLE: Record<string, string> = {
  local: 'bg-primary/15 text-primary',
  'local-optional': 'bg-primary/15 text-primary',
  external: 'bg-sky-500/15 text-sky-600 dark:text-sky-400',
  'auth-required': 'bg-sky-500/15 text-sky-600 dark:text-sky-400',
  'security-tool': 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
};

function DemoChatBubble({ msg }: { msg: DemoMessage }) {
  // System messages (workflow steps, status updates)
  if (msg.role === 'system') {
    return (
      <div className="flex justify-center">
        <div className="max-w-[90%] rounded-lg bg-muted/30 border border-border/30 px-3 py-2">
          {msg.workflowStep && (
            <div className="flex items-center gap-2 text-xs">
              <div className="flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 shrink-0">
                {msg.workflowStep.status === 'done' ? (
                  <Check className="h-3 w-3 text-primary" />
                ) : msg.workflowStep.status === 'running' ? (
                  <Loader2 className="h-3 w-3 text-primary animate-spin" />
                ) : (
                  <Clock className="h-3 w-3 text-muted-foreground/40" />
                )}
              </div>
              <span className="font-medium text-foreground">
                步骤 {msg.workflowStep.stepNumber}: {msg.workflowStep.stepName}
              </span>
              {msg.workflowStep.description && <span className="text-muted-foreground">· {msg.workflowStep.description}</span>}
            </div>
          )}
          {msg.content && !msg.workflowStep && <p className="text-xs text-muted-foreground text-center">{msg.content}</p>}
        </div>
      </div>
    );
  }

  // User messages
  if (msg.role === 'user') {
    return (
      <div className="group px-1 mb-3">
        <div className="flex justify-end">
          <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-[oklch(0.52_0.17_160)] px-4 py-2.5 shadow-lg">
            <p className="text-sm text-primary-foreground leading-relaxed whitespace-pre-wrap">{msg.content}</p>
          </div>
        </div>
      </div>
    );
  }

  // Assistant messages (with rich content support)
  return (
    <div className="group px-1 mb-3">
      {/* Tool calls — 与 workplace 一致：7rem 偏移 + flex-1 */}
      {msg.toolCalls && msg.toolCalls.length > 0 && (
        <div className="flex gap-3 items-start mb-1.5">
          <div className="shrink-0 w-7" />
          <div className="flex-1 min-w-0 flex flex-col gap-0.5">
            {msg.toolCalls.map((tc, tcIdx) => {
              const match = matchToolCapsule(tc.tool, {});
              return (
                <ToolCallItem
                  key={tcIdx}
                  tool={{
                    id: `demo-tool-${tcIdx}`,
                    label: match?.label ?? tc.tool,
                    paramLabel: match?.paramLabel ?? tc.result,
                    status: tc.status,
                    matchType: match?.type,
                  }}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Main bubble: avatar + content */}
      {(msg.content || msg.thinking || (msg.subAgents && msg.subAgents.length > 0) || (msg.richCards && msg.richCards.length > 0) || (msg.summaryItems && msg.summaryItems.length > 0)) && (
        <div className="flex gap-3 items-start">
          <div className="shrink-0 mt-0.5 w-7">
            <AgentAvatar />
          </div>
          <div className="max-w-[calc(100%-3rem)] flex flex-col gap-2">
            {/* Thinking indicator */}
            {msg.thinking && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground px-2">
                <Loader2 className="h-3 w-3 animate-spin" />
                <span>思考中...</span>
              </div>
            )}

            {/* Main text content */}
            {msg.content && (
              <div className="rounded-2xl rounded-tl-sm bg-secondary/50 backdrop-blur-sm border border-border/40 px-4 py-2.5 shadow-sm">
                <ChatMarkdown content={msg.content} />
              </div>
            )}

            {/* Skills-loader compact display */}
            {msg.subAgents &&
              msg.subAgents.length > 0 &&
              (() => {
                const skillsLoader = msg.subAgents.find((a) => 'id' in a && a.id === 'skills-loader' && 'skills' in a);
                if (!skillsLoader || !('skills' in skillsLoader)) return null;
                const agent = skillsLoader as typeof skillsLoader & { skills: Array<{ status: string; privacy?: string }> };
                const allDone = agent.skills.every((s: { status: string }) => s.status === 'done');
                if (!allDone) return null;
                return (
                  <div className="rounded-xl px-3.5 py-2">
                    <div className="flex items-center gap-2">
                      <div className="flex -space-x-1">
                        {agent.skills.map((skill: { status: string; privacy?: string }, sIdx: number) => {
                          const privacy = skill.privacy as string | undefined;
                          const Icon = privacy ? (PRIVACY_ICON[privacy] ?? ShieldCheck) : ShieldCheck;
                          const orbStyle = privacy ? (PRIVACY_ORB_STYLE[privacy] ?? 'bg-muted text-muted-foreground') : 'bg-primary/15 text-primary';
                          return (
                            <div className={cn('h-4 w-4 rounded-full ring-[1.5px] ring-background flex items-center justify-center', orbStyle)} key={sIdx}>
                              <Icon className="h-2 w-2" />
                            </div>
                          );
                        })}
                      </div>
                      <span className="text-caption text-foreground/70">已就绪 {agent.skills.length} 个 Skills</span>
                      <Check className="h-3 w-3 text-violet-500 dark:text-violet-400" />
                    </div>
                  </div>
                );
              })()}

            {/* Sub-agents (excluding skills-loader) */}
            {msg.subAgents && msg.subAgents.length > 0 && (
              <div className="flex flex-col gap-1.5">
                {msg.subAgents.map((agent, agIdx) => {
                  const isFullAgent = 'id' in agent && 'skills' in agent;
                  const currentAction = !isFullAgent && 'currentAction' in agent ? agent.currentAction : undefined;
                  const isSkillsLoader = isFullAgent && 'id' in agent && agent.id === 'skills-loader';
                  if (isSkillsLoader) return null;

                  return (
                    <div className="flex items-center gap-2 rounded-lg bg-card/70 border border-border/40 px-3 py-2 text-xs" key={agIdx}>
                      {agent.status === 'done' ? (
                        <Check className="h-3 w-3 text-primary shrink-0" />
                      ) : agent.status === 'running' ? (
                        <Loader2 className="h-3 w-3 text-primary animate-spin shrink-0" />
                      ) : (
                        <Clock className="h-3 w-3 text-muted-foreground/40 shrink-0" />
                      )}
                      <span className="font-medium text-foreground">{agent.name}</span>
                      <span className="text-muted-foreground/60">· {agent.role}</span>
                      {currentAction && (
                        <>
                          <ChevronRight className="h-3 w-3 text-muted-foreground/30" />
                          <span className="text-muted-foreground/80 truncate">{currentAction}</span>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Rich cards */}
            {msg.richCards && msg.richCards.length > 0 && (
              <div className="flex flex-col gap-2">
                {msg.richCards.map((card, cardIdx) => (
                  <RichCardRenderer card={card} key={cardIdx} />
                ))}
              </div>
            )}

            {/* Summary items */}
            {msg.summaryItems && msg.summaryItems.length > 0 && (
              <div className="rounded-lg bg-primary/5 border border-primary/20 px-3 py-2.5">
                <div className="flex flex-col gap-1.5">
                  {msg.summaryItems.map((item, itemIdx) => (
                    <div className="flex items-start gap-2 text-xs" key={itemIdx}>
                      <Check className="h-3 w-3 text-primary shrink-0 mt-0.5" />
                      <span className="text-foreground">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * CasePreviewModal — shows a conversation preview for a community post.
 * Priority: post.aiMessage (real SSE data) → DEMO_CONVERSATIONS (fallback).
 * Renders chat messages in workplace conversation bubble style.
 */
export function CasePreviewModal({ post, onClose }: CasePreviewModalProps) {
  const { closing, requestClose } = useAnimatedClose(onClose, 280);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    setEntered(false);
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, [post.id]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // 优先使用 aiMessage（真实 SSE 数据），否则回退到 demo 数据
  const sseMessages = useMemo(() => (post.aiMessage ? parseAiMessage(post.aiMessage) : []), [post.aiMessage]);
  const demoMessages = useMemo(() => (sseMessages.length === 0 ? (DEMO_CONVERSATIONS[post.id] ?? []) : []), [sseMessages.length, post.id]);
  const hasMessages = sseMessages.length > 0 || demoMessages.length > 0;
  const typeConfig = ABILITY_TYPE_CONFIG[post.abilityType];

  const panelStyle = {
    opacity: closing ? 0 : entered ? 1 : 0,
    transform: closing ? 'scale(0.96) translateY(8px)' : entered ? 'scale(1) translateY(0)' : 'scale(0.92) translateY(12px)',
    filter: closing ? 'blur(4px)' : entered ? 'blur(0)' : 'blur(6px)',
    transition: 'opacity 280ms, transform 280ms, filter 280ms',
    transitionTimingFunction: closing ? 'cubic-bezier(0.4, 0, 1, 1)' : 'cubic-bezier(0.16, 1, 0.3, 1)',
  };

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        aria-hidden
        className={`liquid-glass-overlay fixed inset-0 transition-all duration-300 ease-out ${closing ? 'opacity-0 backdrop-blur-none' : entered ? 'opacity-100 backdrop-blur-xl' : 'opacity-0 backdrop-blur-none'
          }`}
        onClick={requestClose}
        style={{ zIndex: Z.MODAL_NESTED, pointerEvents: closing || !entered ? 'none' : 'auto' }}
      />

      {/* Modal container */}
      <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: Z.MODAL_NESTED + 1, pointerEvents: 'none' }}>
        <div className="w-full max-w-3xl max-h-[85vh] flex flex-col" style={{ ...panelStyle, pointerEvents: 'auto' }}>
          <div className="liquid-glass-modal rounded-2xl flex flex-col overflow-hidden max-h-[85vh]">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-border/50 shrink-0">
              <div className="flex items-center gap-2.5 min-w-0">
                <AbilityTypeBadge size="sm" type={post.abilityType} />
                <h2 className="text-sm font-semibold text-foreground truncate">{post.title}</h2>
              </div>
              <button
                className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors shrink-0 ml-2"
                onClick={requestClose}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* Chat messages */}
            <div className="flex-1 overflow-y-auto no-scrollbar px-4 py-4">
              {!hasMessages ? (
                <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">暂无案例</div>
              ) : sseMessages.length > 0 ? (
                /* ── SSE 真实消息：使用 workplace 对话样式 ── */
                <div className="flex flex-col gap-0">
                  {sseMessages.map((msg) => (
                    <SseChatBubble key={msg.messageId} msg={msg} />
                  ))}
                </div>
              ) : (
                /* ── Demo 消息：原有渲染逻辑 ── */
                <div className="flex flex-col gap-3">
                  {demoMessages.map((msg: DemoMessage, idx: number) => (
                    <DemoChatBubble key={idx} msg={msg} />
                  ))}
                </div>
              )}
            </div>

            {/* Footer hint */}
            <div className="px-5 py-3 border-t border-border/50 shrink-0">
              <p className="text-caption text-muted-foreground/60 text-center">以上为「{typeConfig.labelZh}」使用案例演示</p>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
